import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, EmptyState, Pill } from "@/components/common";
import { Plus, Trash2, Pencil, MapPin, ChevronLeft, ChevronRight, Clock, CalendarDays } from "lucide-react";

const TIPI = ["Campo", "Palestra", "Spogliatoio", "Sala", "Piscina", "Altro"];
const COLORS = ["bg-sky-100 text-sky-800 border-sky-300", "bg-emerald-100 text-emerald-800 border-emerald-300", "bg-violet-100 text-violet-800 border-violet-300", "bg-amber-100 text-amber-800 border-amber-300", "bg-rose-100 text-rose-800 border-rose-300", "bg-teal-100 text-teal-800 border-teal-300"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
const isoDate = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };

export default function Impianti() {
  const { can } = useAuth();
  const editable = can("impianti", "edit");
  const [impianti, setImpianti] = useState(null);
  const [pren, setPren] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [impOpen, setImpOpen] = useState(false);
  const [impForm, setImpForm] = useState({ nome: "", tipo: "Campo", note: "" });
  const [impEdit, setImpEdit] = useState(null);
  const [preOpen, setPreOpen] = useState(false);
  const [preForm, setPreForm] = useState({ impianto_id: "", corso_id: "", titolo: "", data: isoDate(new Date()), start: "17:00", end: "18:30" });
  const [corsi, setCorsi] = useState([]);

  const load = () => {
    api.get("/impianti").then((r) => setImpianti(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/prenotazioni").then((r) => setPren(r.data)).catch(() => {});
    api.get("/corsi").then((r) => setCorsi(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const colorOf = (impId) => COLORS[Math.max(0, (impianti || []).findIndex((i) => i.id === impId)) % COLORS.length];
  const days = Array.from({ length: 7 }).map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });

  const saveImp = async () => {
    if (!impForm.nome) { toast.error("Nome obbligatorio"); return; }
    try { if (impEdit) await api.put(`/impianti/${impEdit}`, impForm); else await api.post("/impianti", impForm); toast.success("Salvato"); setImpOpen(false); setImpEdit(null); setImpForm({ nome: "", tipo: "Campo", note: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delImp = async (id) => { if (!window.confirm("Eliminare lo spazio e le sue prenotazioni?")) return; await api.delete(`/impianti/${id}`); load(); };
  const savePre = async () => {
    if (!preForm.impianto_id || !preForm.titolo) { toast.error("Impianto e titolo obbligatori"); return; }
    try { await api.post("/prenotazioni", preForm); toast.success("Prenotazione creata"); setPreOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delPre = async (id) => { await api.delete(`/prenotazioni/${id}`); load(); };
  const openPre = (dateStr) => { setPreForm((f) => ({ ...f, data: dateStr || isoDate(new Date()), impianto_id: impianti?.[0]?.id || "", titolo: "", corso_id: "" })); setPreOpen(true); };

  if (!impianti) return <Spinner />;

  return (
    <div>
      <PageHeader title="Gestione Impianti" subtitle="Spazi, campi e calendario con blocco anti-sovrapposizione"
        actions={editable && <>
          <Button variant="outline" onClick={() => { setImpForm({ nome: "", tipo: "Campo", note: "" }); setImpEdit(null); setImpOpen(true); }} data-testid="add-impianto-btn"><Plus size={16} /> Nuovo spazio</Button>
          <Button onClick={() => openPre()} data-testid="add-prenotazione-btn"><Plus size={16} /> Prenotazione</Button>
        </>} />

      {/* Spazi */}
      <Card className="p-4 mb-5">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Spazi disponibili</p>
        {impianti.length === 0 ? <EmptyState icon={MapPin} title="Nessuno spazio" hint="Aggiungi campi, palestre, spogliatoi..." /> : (
          <div className="flex flex-wrap gap-2">
            {impianti.map((im) => (
              <div key={im.id} className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg border ${colorOf(im.id)}`} data-testid={`impianto-${im.id}`}>
                <MapPin size={14} /><span className="text-sm font-medium">{im.nome}</span><span className="text-xs opacity-70">· {im.tipo}</span>
                {editable && <>
                  <button onClick={() => { setImpForm({ nome: im.nome, tipo: im.tipo, note: im.note || "" }); setImpEdit(im.id); setImpOpen(true); }} className="p-0.5 hover:opacity-70"><Pencil size={13} /></button>
                  <button onClick={() => delImp(im.id)} data-testid={`delete-impianto-${im.id}`} className="p-0.5 hover:opacity-70"><Trash2 size={13} /></button>
                </>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Calendario settimanale */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-slate-700 font-semibold font-display"><CalendarDays size={18} /> Settimana del {days[0].toLocaleDateString("it-IT", { day: "2-digit", month: "long" })}</div>
          <div className="flex gap-1">
            <button onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })} data-testid="week-prev" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Oggi</button>
            <button onClick={() => setWeekStart((w) => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })} data-testid="week-next" className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {days.map((d, i) => {
            const ds = isoDate(d);
            const dayPren = pren.filter((p) => p.data === ds).sort((a, b) => a.start.localeCompare(b.start));
            const isToday = ds === isoDate(new Date());
            return (
              <div key={i} className={`rounded-lg border min-h-[130px] ${isToday ? "border-sky-300 bg-sky-50/40" : "border-slate-200"}`} data-testid={`day-col-${ds}`}>
                <div className="px-2 py-1.5 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600">{GIORNI[i]} {d.getDate()}</span>
                  {editable && <button onClick={() => openPre(ds)} className="text-slate-300 hover:text-sky-600"><Plus size={14} /></button>}
                </div>
                <div className="p-1.5 space-y-1.5">
                  {dayPren.length === 0 ? <p className="text-[11px] text-slate-300 text-center py-3">—</p> : dayPren.map((p) => (
                    <div key={p.id} className={`rounded-md border px-2 py-1 text-[11px] ${colorOf(p.impianto_id)}`} data-testid={`pren-${p.id}`}>
                      <div className="flex items-center gap-1 font-semibold"><Clock size={10} /> {p.start}–{p.end}</div>
                      <div className="font-medium truncate">{p.titolo}</div>
                      <div className="flex items-center justify-between opacity-80"><span className="truncate">{p.impianto_name}</span>{editable && <button onClick={() => delPre(p.id)} className="hover:opacity-60"><Trash2 size={11} /></button>}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Modal open={impOpen} onClose={() => setImpOpen(false)} title={impEdit ? "Modifica spazio" : "Nuovo spazio"}>
        <div className="space-y-4">
          <Input label="Nome" data-testid="impianto-nome" value={impForm.nome} onChange={(e) => setImpForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Es. Campo 1, Palestra..." />
          <Select label="Tipo" value={impForm.tipo} onChange={(e) => setImpForm((f) => ({ ...f, tipo: e.target.value }))}>{TIPI.map((t) => <option key={t}>{t}</option>)}</Select>
          <Input label="Note" value={impForm.note} onChange={(e) => setImpForm((f) => ({ ...f, note: e.target.value }))} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setImpOpen(false)}>Annulla</Button><Button onClick={saveImp} data-testid="save-impianto-btn">Salva</Button></div>
        </div>
      </Modal>

      <Modal open={preOpen} onClose={() => setPreOpen(false)} title="Nuova prenotazione">
        <div className="space-y-4">
          <Select label="Impianto / Spazio" data-testid="pren-impianto" value={preForm.impianto_id} onChange={(e) => setPreForm((f) => ({ ...f, impianto_id: e.target.value }))}>
            <option value="">Seleziona...</option>{impianti.map((im) => <option key={im.id} value={im.id}>{im.nome} ({im.tipo})</option>)}
          </Select>
          <Input label="Titolo" data-testid="pren-titolo" value={preForm.titolo} onChange={(e) => setPreForm((f) => ({ ...f, titolo: e.target.value }))} placeholder="Es. Allenamento U15" />
          <Select label="Corso collegato (opz.)" value={preForm.corso_id} onChange={(e) => setPreForm((f) => ({ ...f, corso_id: e.target.value }))}><option value="">—</option>{corsi.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}</Select>
          <div className="grid grid-cols-3 gap-3">
            <Input label="Data" type="date" data-testid="pren-data" value={preForm.data} onChange={(e) => setPreForm((f) => ({ ...f, data: e.target.value }))} />
            <Input label="Inizio" type="time" data-testid="pren-start" value={preForm.start} onChange={(e) => setPreForm((f) => ({ ...f, start: e.target.value }))} />
            <Input label="Fine" type="time" data-testid="pren-end" value={preForm.end} onChange={(e) => setPreForm((f) => ({ ...f, end: e.target.value }))} />
          </div>
          <p className="text-xs text-slate-400">Il sistema blocca automaticamente le sovrapposizioni sullo stesso spazio.</p>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPreOpen(false)}>Annulla</Button><Button onClick={savePre} data-testid="save-prenotazione-btn">Prenota</Button></div>
        </div>
      </Modal>
    </div>
  );
}
